/**
 * 消息气泡组件
 * 区分员工/外部联系人，支持多种消息类型
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Tooltip,
  SxProps,
  Theme,
  CircularProgress,
  Link,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Image as ImageIcon,
  BrokenImage as BrokenImageIcon,
  InsertDriveFile as FileIcon,
  PlayCircle as VideoIcon,
  LocationOn as LocationIcon,
  CardGiftcard as RedPacketIcon,
  Person as CardIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  Link as LinkIcon,
  Call as CallIcon,
  Event as EventIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import type { Message } from '../../types/conversation';
import { apiClient } from '../../services/apiClient';
import { API_ENDPOINTS } from '../../services/endpoints';
import { renderWeComUserNameHtml } from '../../utils/wecomOpenData';
import { isExternalWeComUserId } from '../../utils/wecomIds';
import { formatDateTime } from '../../utils/format';
import { WeComMessageDisplay } from './WeComMessageDisplay';
import { WeComAvatar } from '../common/WeComAvatar';

interface MessageBubbleProps {
  message: Message;
  sx?: SxProps<Theme>;
}

const mediaRequestConfig = {
  responseType: 'blob' as const,
  timeout: 120000,
  suppressErrorToast: true,
};

// 解析 Axios 错误返回的友好提示信息（兼容 Blob 类型的 JSON 返回）
const getFriendlyErrorMessage = async (err: any): Promise<string> => {
  const rawMessage = String(err?.message || '');
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(rawMessage)) {
    return '媒体文件下载超时，请稍后重试';
  }
  if (err?.response?.status === 410) {
    return '该媒体文件已过期失效，无法下载';
  }
  if (err?.response?.status === 404) {
    return '媒体文件暂时不可用或已被清理';
  }

  const response = err?.response;
  if (!response) return rawMessage || '请求失败';

  let data = response.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      data = JSON.parse(text);
    } catch {
      // ignore
    }
  }

  return data?.message || data?.detail || rawMessage || '请求失败';
};

const readBlobApiMessage = async (blob: Blob): Promise<string | null> => {
  const contentType = blob.type || '';
  if (!contentType.includes('application/json') && !contentType.includes('text/')) {
    return null;
  }
  try {
    const text = await blob.text();
    const data = JSON.parse(text);
    return data?.message || data?.detail || null;
  } catch {
    return null;
  }
};

export const parseMaybeJson = (value: unknown): Record<string, any> | null => {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, any>;
  }
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, any>
      : null;
  } catch {
    return null;
  }
};

export const extractRoomOpenId = (message: Message): string => {
  const contentJson = parseMaybeJson(message.content_json);
  const rawData = parseMaybeJson(contentJson?.raw_data);
  const candidates = [contentJson, rawData].filter(Boolean) as Record<string, any>[];
  for (const data of candidates) {
    const roomId = String(data.roomid || data.room_id || data.chatid || data.chat_id || '');
    if (roomId) return roomId;
  }
  return '';
};

export const getSenderOpenDataId = (message: Message): string => {
  const senderOpenId = String(
    message.sender_type === 'employee'
      ? message.sender_open_userid || ''
      : message.sender_wecom_userid || message.sender_id || '',
  );
  const roomId = extractRoomOpenId(message);
  const senderIsExternal = message.sender_type === 'external'
    || (!message.sender_type && isExternalWeComUserId(senderOpenId));
  if (roomId && senderOpenId === roomId) return '';
  if (
    roomId
    && senderIsExternal
    && !senderOpenId.includes('/')
  ) {
    return `${roomId}/${senderOpenId}`;
  }
  return senderOpenId;
};

const WECOM_MESSAGE_TYPE_MAP: Record<string, string> = {
  '0': 'unsupported',
  '1': 'text',
  '2': 'image',
  '3': 'emotion',
  '4': 'link',
  '5': 'weapp',
  '6': 'voice',
  '7': 'video',
  '8': 'file',
  '9': 'card',
  '10': 'chatrecord',
  '11': 'sphfeed',
  '12': 'calendar',
  '13': 'redpacket',
  '14': 'location',
  '15': 'meeting',
  '16': 'todo',
  '17': 'vote',
  '18': 'docmsg',
  '19': 'news',
  '20': 'mixed',
  '21': 'audio_archive',
  '22': 'meeting_voice_call',
  '23': 'qydiskfile',
  '24': 'agree',
  '25': 'disagree',
  '26': 'solitaire',
  '27': 'markdown',
  '28': 'note',
};

const normalizeWeComMessageType = (value: unknown, fallback: unknown = ''): string => {
  const raw = String(value || fallback || '').trim().toLowerCase();
  return WECOM_MESSAGE_TYPE_MAP[raw] || raw;
};

const extractMessageSdkFileId = (message: Message): string => {
  const contentJson = parseMaybeJson(message.content_json);
  const rawData = parseMaybeJson(contentJson?.raw_data);
  const candidates = [rawData, contentJson].filter(Boolean) as Record<string, any>[];

  for (const data of candidates) {
    const msgType = normalizeWeComMessageType(data.msgtype, message.msg_type);
    const typedNode = msgType ? data[msgType] : null;
    if (typedNode && typeof typedNode === 'object') {
      const sdkfileid = String(typedNode.sdkfileid || typedNode.sdk_file_id || '');
      if (sdkfileid) return sdkfileid;
      const voicefile = typedNode.voicefile;
      if (voicefile && typeof voicefile === 'object') {
        const voiceSdkFileId = String(voicefile.sdkfileid || voicefile.sdk_file_id || '');
        if (voiceSdkFileId) return voiceSdkFileId;
      }
    }
    const sdkfileid = String(data.sdkfileid || data.sdk_file_id || '');
    if (sdkfileid) return sdkfileid;
  }

  return '';
};

const hasPlayableAudioMedia = (message: Message): boolean => Boolean(
  message.media_url
  || extractMessageSdkFileId(message)
);

const getMessagePayloadCandidates = (message: Message): Record<string, any>[] => {
  const contentJson = parseMaybeJson(message.content_json);
  const rawData = parseMaybeJson(contentJson?.raw_data);
  return [rawData, contentJson].filter(Boolean) as Record<string, any>[];
};

export const extractWeComDisplayInfo = (message: Message): { msgid: string; secretKey: string } | null => {
  const contentJson = parseMaybeJson(message.content_json);
  if (!contentJson) {
    const msgid = String(message.msg_id || '');
    const secretKey = String((message as unknown as Record<string, unknown>).secret_key || '');
    return msgid && secretKey ? { msgid, secretKey } : null;
  }

  const display = parseMaybeJson(contentJson.wecom_display);
  const serviceEncryptInfo = parseMaybeJson(contentJson.service_encrypt_info);
  const rawData = parseMaybeJson(contentJson.raw_data);
  const rawServiceEncryptInfo = parseMaybeJson(rawData?.service_encrypt_info);
  const rawDisplay = parseMaybeJson(rawData?.wecom_display);

  const secretKey = String(
    display?.secret_key
    || rawDisplay?.secret_key
    || serviceEncryptInfo?.secret_key
    || rawServiceEncryptInfo?.secret_key
    || contentJson.secret_key
    || rawData?.secret_key
    || (message as unknown as Record<string, unknown>).secret_key
    || ''
  );
  const msgid = String(
    display?.msgid
    || rawDisplay?.msgid
    || contentJson.msgid
    || rawData?.msgid
    || message.msg_id
    || ''
  );

  if (!msgid || !secretKey) return null;
  return { msgid, secretKey };
};

export const WECOM_NATIVE_DISPLAY_TYPES = new Set([
  'image',
  'emotion',
  'link',
  'weapp',
  'miniprogram',
  'voice',
  'video',
  'file',
  'card',
  'chatrecord',
  'forward',
  'sphfeed',
  'sph',
  'calendar',
  'redpacket',
  'location',
  'meeting',
  'todo',
  'vote',
  'docmsg',
  'news',
  'mixed',
  'audio_archive',
  'meeting_voice_call',
  'meeting_voicecall',
  'qydiskfile',
  'voip_doc_share',
  'voipdocshare',
  'agree',
  'disagree',
  'solitaire',
  'collect',
  'markdown',
  'note',
]);

export const WECOM_PREVIEWABLE_TYPES = new Set([
  'image',
  'video',
  'chatrecord',
  'forward',
]);

const renderWeComNativeMessage = (message: Message, isSelf: boolean): React.ReactNode | null => {
  const msgType = normalizeWeComMessageType(message.msg_type);
  if (!WECOM_NATIVE_DISPLAY_TYPES.has(msgType)) {
    return null;
  }

  const displayInfo = extractWeComDisplayInfo(message);
  if (!displayInfo) return null;

  return (
    <WeComMessageDisplay
      msgid={displayInfo.msgid}
      secretKey={displayInfo.secretKey}
      fallback={message.content || `[${message.msg_type}消息]`}
      isSelf={isSelf}
      displayType="normal"
      previewable={WECOM_PREVIEWABLE_TYPES.has(msgType)}
      messageType={msgType}
    />
  );
};

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

// 微信/企微常用自带表情文本到标准 Emoji 字符映射表
const wechatEmoticonMap: Record<string, string> = {
  '[微笑]': '😊', '[撇嘴]': '😕', '[色]': '😍', '[发呆]': '😳', '[得意]': '😎',
  '[流泪]': '😭', '[害羞]': '😳', '[闭嘴]': '🤐', '[睡]': '😴', '[大哭]': '😭',
  '[尴尬]': '😅', '[发怒]': '😡', '[调皮]': '😜', '[呲牙]': '😁', '[惊讶]': '😱',
  '[难过]': '😔', '[酷]': '😎', '[冷汗]': '😰', '[抓狂]': '😫', '[吐]': '🤢',
  '[偷笑]': '🤭', '[愉快]': '😊', '[白眼]': '🙄', '[傲慢]': '😒', '[饥饿]': '🤤',
  '[困]': '🥱', '[惊恐]': '😱', '[流汗]': '😓', '[憨笑]': '😄', '[悠闲]': '😌',
  '[奋斗]': '😫', '[咒骂]': '🤬', '[疑问]': '❓', '[嘘]': '🤫', '[晕]': '😵',
  '[疯了]': '🤪', '[衰]': '😔', '[骷髅]': '💀', '[敲打]': '🔨', '[再见]': '👋',
  '[擦汗]': '😓', '[抠鼻]': '👃', '[鼓掌]': '👏', '[糗大了]': '🤦', '[坏笑]': '😏',
  '[左哼哼]': '😒', '[右哼哼]': '😒', '[哈欠]': '🥱', '[鄙视]': '😒', '[委屈]': '🥺',
  '[快哭了]': '😭', '[阴险]': '😏', '[亲亲]': '😘', '[吓]': '😱', '[可怜]': '🥺',
  '[菜刀]': '🔪', '[西瓜]': '🍉', '[啤酒]': '🍺', '[篮球]': '🏀', '[乒乓]': '🏓',
  '[咖啡]': '☕', '[饭]': '🍚', '[猪头]': '🐷', '[玫瑰]': '🌹', '[凋谢]': '🥀',
  '[嘴唇]': '👄', '[爱心]': '❤️', '[心碎]': '💔', '[蛋糕]': '🎂', '[闪电]': '⚡',
  '[炸弹]': '💣', '[刀]': '🔪', '[足球]': '⚽', '[瓢虫]': '🐞', '[便便]': '💩',
  '[月亮]': '🌙', '[太阳]': '☀️', '[礼物]': '🎁', '[拥抱]': '🤗', '[强]': '👍',
  '[弱]': '👎', '[握手]': '🤝', '[胜利]': '✌️', '[抱拳]': '🙏', '[勾引]': '🤙',
  '[拳头]': '✊', '[差劲]': '👎', '[爱你]': '🤟', '[NO]': '🙅', '[OK]': '🙆',
  '[爱情]': '💏', '[飞吻]': '💋', '[跳跳]': '💃', '[发抖]': '📳', '[怄火]': '😡',
  '[转圈]': '💃',
  '[破涕为笑]': '😂', '[笑哭]': '😂', '[捂脸]': '🤦', '[嘿哈]': '🤣',
  '[机智]': '😏', '[耶]': '✌️', '[吃瓜]': '🍉', '[加油]': '💪',
  '[打脸]': '🤦', '[皱眉]': '😕', '[失望]': '😞', '[礼貌微笑]': '🙂'
};

export const replaceWeChatEmoticons = (text: string): string => {
  if (!text) return text;
  return text.replace(/\[[^\]]+\]/g, (match) => {
    return wechatEmoticonMap[match] || match;
  });
};

// 解析引用/回复消息
interface ParsedQuoteMessage {
  hasQuote: boolean;
  quoteSender?: string;
  quoteText?: string;
  replyText: string;
}

const parseQuoteMessage = (text: string): ParsedQuoteMessage => {
  if (!text) return { hasQuote: false, replyText: text };

  // 微信/企微文本引用格式正则：
  // 1. 兼容可选的首行虚线前缀
  // 2. 兼容可选的“这是一条引用/回复消息：”或“引用...消息：”提示语
  // 3. 支持“”、「」、""、''多种包裹引号，提取引用块内容
  // 4. 支持由连续中划线（---）或带空格中划线（- - - -）组成的分割线
  // 5. 提取分割线后的回复内容（如果有的话）
  const regex = /^(?:-+\r?\n)?(?:(?:这是一条引用\/回复消息：|引用.*?消息：)\r?\n)?(?:“|'|"|「)([\s\S]+?)(?:”|'|"|」)\r?\n[ \t]*(?:-\s*){3,}-*(?:\r?\n([\s\S]*))?$/i;
  const match = text.trim().match(regex);
  if (match) {
    const quoteContent = match[1];
    const replyText = match[2] || '';
    
    const quoteLines = quoteContent.split(/\r?\n/);
    let quoteSender = '';
    let quoteText = '';
    
    if (quoteLines.length > 0) {
      const firstLine = quoteLines[0];
      const colonIndex = firstLine.search(/[:：]/);
      if (colonIndex !== -1) {
        quoteSender = firstLine.substring(0, colonIndex).trim();
        const firstLineText = firstLine.substring(colonIndex + 1).trim();
        const otherLines = quoteLines.slice(1);
        if (firstLineText) {
          quoteText = [firstLineText, ...otherLines].join('\n');
        } else {
          quoteText = otherLines.join('\n');
        }
      } else {
        quoteSender = firstLine.trim();
        quoteText = quoteLines.slice(1).join('\n');
      }
    }
    
    return {
      hasQuote: true,
      quoteSender,
      quoteText,
      replyText: replyText.trim()
    };
  }
  
  return { hasQuote: false, replyText: text };
};

// 1. 文本超链接自动转换渲染器
const LinkifyText: React.FC<{ text: string; isSelf: boolean }> = ({ text, isSelf }) => {
  const { hasQuote, quoteSender, quoteText, replyText } = parseQuoteMessage(text);
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = replyText.split(urlRegex);

  const renderTextContent = () => (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'inline' }}>
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          return (
            <Link
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                wordBreak: 'break-all',
                fontWeight: 500,
                '&:hover': { color: 'primary.dark' }
              }}
            >
              {part}
            </Link>
          );
        }
        return replaceWeChatEmoticons(part);
      })}
    </Typography>
  );

  if (!hasQuote) {
    return renderTextContent();
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* 引用栏 */}
      <Box
        sx={{
          borderLeft: '3px solid',
          borderColor: isSelf ? 'rgba(30, 136, 229, 0.28)' : 'rgba(0, 0, 0, 0.22)',
          bgcolor: isSelf ? 'rgba(255, 255, 255, 0.38)' : 'rgba(255, 255, 255, 0.42)',
          p: 1,
          pl: 1.5,
          mb: 1.2,
          borderRadius: 1,
          fontSize: '0.8rem',
          color: 'text.secondary',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            display: 'block',
            mb: 0.25,
            color: 'text.primary',
          }}
        >
          {quoteSender}
        </Typography>
        {replaceWeChatEmoticons(quoteText || '')}
      </Box>
      
      {/* 回复内容 */}
      {renderTextContent()}
    </Box>
  );
};

// 语音气泡声波图标组件
const VoiceIcon: React.FC<{ playing: boolean; isSelf: boolean }> = ({ playing, isSelf }) => {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{
        width: 18,
        height: 18,
        color: isSelf ? '#fff' : 'text.primary',
        transform: isSelf ? 'rotate(180deg)' : 'none',
        flexShrink: 0,
        display: 'block',
      }}
    >
      {/* Wave 1 */}
      <Box
        component="path"
        d="M 6 9 A 4 4 0 0 1 6 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
        sx={playing ? {
          animation: 'voiceWave1 1.2s infinite',
          '@keyframes voiceWave1': {
            '0%, 100%': { opacity: 0.2 },
            '30%, 90%': { opacity: 1 },
          }
        } : { opacity: 1 }}
      />
      {/* Wave 2 */}
      <Box
        component="path"
        d="M 11 6 A 8 8 0 0 1 11 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
        sx={playing ? {
          animation: 'voiceWave2 1.2s infinite',
          '@keyframes voiceWave2': {
            '0%, 30%, 100%': { opacity: 0.2 },
            '60%, 90%': { opacity: 1 },
          }
        } : { opacity: 1 }}
      />
      {/* Wave 3 */}
      <Box
        component="path"
        d="M 16 3 A 12 12 0 0 1 16 21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
        sx={playing ? {
          animation: 'voiceWave3 1.2s infinite',
          '@keyframes voiceWave3': {
            '0%, 60%, 100%': { opacity: 0.2 },
            '90%': { opacity: 1 },
          }
        } : { opacity: 1 }}
      />
    </Box>
  );
};

// 2. 内嵌安全音频播放器
const MediaAudio: React.FC<{ message: Message; isSelf: boolean; label?: string }> = ({ message, isSelf, label }) => {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const handlePlayToggle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡
    const mediaMessageId = message.media_message_id || message.id;
    if (!mediaMessageId) return;
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }

    if (blobUrl) {
      audioRef.current?.play();
      setPlaying(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      let blob: Blob | null = null;
      let pendingMessage = '';
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const result = await apiClient.get(
          API_ENDPOINTS.CONVERSATIONS.MEDIA(mediaMessageId),
          mediaRequestConfig as any
        );
        const nextBlob = result as unknown as Blob;
        if (nextBlob.size === 0) throw new Error('音频数据为空');
        const apiMessage = await readBlobApiMessage(nextBlob);
        if (!apiMessage) {
          blob = nextBlob;
          break;
        }
        pendingMessage = apiMessage;
        if (!apiMessage.includes('正在后台下载') || attempt === 2) {
          throw new Error(apiMessage);
        }
        await wait(1600);
      }

      if (!blob) throw new Error(pendingMessage || '音频加载失败');
      
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setPlaying(false);
      };
      audio.onerror = () => {
        setError('播放错误');
        setPlaying(false);
      };
      audio.play();
      setPlaying(true);
    } catch (err: any) {
      console.error('[MediaAudio] 加载失败:', err);
      const errMsg = await getFriendlyErrorMessage(err);
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
      audioRef.current?.pause();
    };
  }, [blobUrl]);

  // 根据语音时长计算气泡宽度 (最小 80px，最大 220px)
  const duration = message.media_duration || message.duration || 0;
  const displayText = label || (duration ? `${Math.round(duration)}"` : '语音');
  const bubbleWidth = label ? Math.min(Math.max(displayText.length * 13 + 44, 160), 320) : 80 + Math.min(duration, 60) * 2.3;

  return (
    <Box
      onClick={handlePlayToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: isSelf ? 'row-reverse' : 'row',
        width: bubbleWidth,
        maxWidth: '100%',
        cursor: 'pointer',
        py: 0.5,
        userSelect: 'none',
        '&:hover': {
          opacity: 0.85,
        },
      }}
    >
      {/* 动画声波或加载状态 */}
      {loading ? (
        <CircularProgress size={16} color="inherit" sx={{ mx: 0.5 }} />
      ) : (
        <VoiceIcon playing={playing} isSelf={isSelf} />
      )}

      {/* 语音时长文本 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isSelf ? 'flex-start' : 'flex-end' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: 'inherit',
            fontSize: '0.875rem',
            lineHeight: 1,
          }}
        >
          {displayText}
        </Typography>
        {error && (
          <Typography
            variant="caption"
            sx={{
              color: isSelf ? 'rgba(255, 255, 255, 0.85)' : 'error.main',
              fontSize: '0.65rem',
              mt: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// 3. 内嵌安全视频播放器
const MediaVideo: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const handlePlayVideo = async () => {
    if (blobUrl) {
      setVideoOpen(true);
      return;
    }
    if (!message.id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.get(
        API_ENDPOINTS.CONVERSATIONS.MEDIA(message.id),
        mediaRequestConfig as any
      );
      const blob = result as unknown as Blob;
      if (blob.size === 0) throw new Error('视频数据为空');
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setVideoOpen(true);
    } catch (err: any) {
      console.error('[MediaVideo] 视频加载失败:', err);
      const errMsg = await getFriendlyErrorMessage(err);
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <VideoIcon fontSize="medium" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.9)' : 'primary.main' }} />
        <Box>
          <Typography variant="body2" fontWeight={500}>视频消息</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {message.duration && (
              <Typography variant="caption" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary' }}>
                {Math.round(message.duration)}"
              </Typography>
            )}
            <Link
              component="button"
              variant="caption"
              underline="hover"
              disabled={loading}
              onClick={handlePlayVideo}
              sx={{ 
                fontWeight: 600, 
                color: isSelf ? '#dceefe' : 'primary.main',
                '&:hover': { color: isSelf ? '#FFF' : 'primary.dark' } 
              }}
            >
              {loading ? '加载中...' : '播放'}
            </Link>
          </Box>
          {error && (
            <Typography variant="caption" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.85)' : 'error.main', display: 'block' }}>
              {error}
            </Typography>
          )}
        </Box>
      </Box>

      {/* 视频播放弹窗 */}
      <Dialog open={videoOpen} onClose={() => setVideoOpen(false)} maxWidth="md" fullWidth>
        <IconButton
          onClick={() => setVideoOpen(false)}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          {blobUrl && (
            <Box component="video" controls src={blobUrl} autoPlay sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// 4. 流式安全文件下载器
// 原生消息的浏览能力交给企业微信会话展示组件处理；这里仅处理兜底下载。
const MediaFile: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(
    message.media_url === 'EXPIRED' ? '该媒体文件在企业微信服务器端已超过5天有效期，已过期失效无法下载' : null
  );

  const fileName = message.file_name || '未知文件';
  const fileSize = message.file_size;
  const ext = fileName ? fileName.split('.').pop()?.toLowerCase() : '';
  const isExpired = !!error || message.media_url === 'EXPIRED';

  const handleDownload = async () => {
    if (!message.id || isExpired) return;
    setDownloading(true);
    setError(null);
    try {
      const result = await apiClient.get(
        API_ENDPOINTS.CONVERSATIONS.MEDIA(message.id),
        mediaRequestConfig as any
      );
      const blob = result as unknown as Blob;
      if (blob.size === 0) throw new Error('文件数据为空');
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // 延迟释放，确保浏览器已开始处理
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 5000);
    } catch (err: any) {
      console.error('[MediaFile] 操作失败:', err);
      const errMsg = await getFriendlyErrorMessage(err);
      setError(errMsg);
    } finally {
      setDownloading(false);
    }
  };

  // 获取文件类别对应的图标及代表色
  const getFileStyle = () => {
    if (isExpired) {
      return {
        icon: <BrokenImageIcon sx={{ fontSize: 28, color: isSelf ? 'rgba(255, 255, 255, 0.5)' : 'text.disabled' }} />,
        iconBg: isSelf ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
        borderColor: isSelf ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)',
        themeColor: isSelf ? 'rgba(255, 255, 255, 0.5)' : 'text.disabled'
      };
    }

    let icon = <FileIcon sx={{ fontSize: 28, color: '#1976d2' }} />;
    let iconBg = 'rgba(25, 118, 210, 0.08)';
    let themeColor = '#1976d2';

    switch (ext) {
      case 'pdf':
        icon = <FileIcon sx={{ fontSize: 28, color: '#d32f2f' }} />;
        iconBg = 'rgba(211, 47, 47, 0.08)';
        themeColor = '#d32f2f';
        break;
      case 'doc':
      case 'docx':
        icon = <FileIcon sx={{ fontSize: 28, color: '#1976d2' }} />;
        iconBg = 'rgba(25, 118, 210, 0.08)';
        themeColor = '#1976d2';
        break;
      case 'xls':
      case 'xlsx':
        icon = <FileIcon sx={{ fontSize: 28, color: '#2e7d32' }} />;
        iconBg = 'rgba(46, 125, 50, 0.08)';
        themeColor = '#2e7d32';
        break;
      case 'ppt':
      case 'pptx':
        icon = <FileIcon sx={{ fontSize: 28, color: '#ed6c02' }} />;
        iconBg = 'rgba(237, 108, 2, 0.08)';
        themeColor = '#ed6c02';
        break;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        icon = <FileIcon sx={{ fontSize: 28, color: '#9c27b0' }} />;
        iconBg = 'rgba(156, 39, 176, 0.08)';
        themeColor = '#9c27b0';
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'svg':
        icon = <ImageIcon sx={{ fontSize: 28, color: '#00bcd4' }} />;
        iconBg = 'rgba(0, 188, 212, 0.08)';
        themeColor = '#00bcd4';
        break;
      case 'mp3':
      case 'wav':
      case 'wma':
      case 'amr':
        icon = <FileIcon sx={{ fontSize: 28, color: '#009688' }} />;
        iconBg = 'rgba(0, 150, 136, 0.08)';
        themeColor = '#009688';
        break;
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'mkv':
        icon = <VideoIcon sx={{ fontSize: 28, color: '#ff9800' }} />;
        iconBg = 'rgba(255, 152, 0, 0.08)';
        themeColor = '#ff9800';
        break;
      default:
        icon = <FileIcon sx={{ fontSize: 28, color: isSelf ? '#fff' : 'text.secondary' }} />;
        iconBg = isSelf ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.04)';
        themeColor = isSelf ? '#fff' : 'text.secondary';
    }

    if (isSelf) {
      iconBg = 'rgba(255, 255, 255, 0.15)';
      icon = React.cloneElement(icon, { sx: { ...icon.props.sx, color: '#fff' } });
      themeColor = '#fff';
    }

    return {
      icon,
      iconBg,
      borderColor: isSelf ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.08)',
      themeColor
    };
  };

  const fileStyle = getFileStyle();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 280,
        borderRadius: 2,
        border: '1px solid',
        borderColor: fileStyle.borderColor,
        bgcolor: isSelf ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.01)',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        transition: 'all 0.2s ease-in-out',
        ...(!isExpired && {
          cursor: 'pointer',
          '&:hover': {
            bgcolor: isSelf ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.03)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transform: 'translateY(-1px)'
          }
        })
      }}
      onClick={() => {
        if (!isExpired && !downloading) {
          handleDownload();
        }
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, alignItems: 'center' }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '6px',
            bgcolor: fileStyle.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          {fileStyle.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color: isSelf ? '#fff' : 'text.primary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-all',
              lineHeight: 1.3,
              opacity: isExpired ? 0.6 : 1
            }}
            title={fileName}
          >
            {fileName}
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
            {fileSize && (
              <Typography
                variant="caption"
                sx={{
                  color: isSelf ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
                  opacity: isExpired ? 0.6 : 1
                }}
              >
                {(fileSize / 1024).toFixed(1)} KB
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          borderTop: '1px dashed',
          borderColor: fileStyle.borderColor,
          px: 1.5,
          py: 0.8,
          bgcolor: isSelf ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.005)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 32
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {isExpired ? (
          <Typography
            variant="caption"
            sx={{
              color: isSelf ? 'rgba(255,255,255,0.7)' : 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            已过期失效
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Link
                component="button"
                variant="caption"
                disabled={downloading}
                onClick={() => handleDownload()}
                sx={{
                  fontWeight: 600,
                  color: isSelf ? '#fff' : fileStyle.themeColor,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                下载
              </Link>
            </Box>
            {downloading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={10} color="inherit" sx={{ color: isSelf ? '#fff' : 'text.secondary' }} />
                <Typography variant="caption" sx={{ color: isSelf ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                  加载中...
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
      {error && !message.media_url && (
        <Box sx={{ px: 1.5, pb: 1, borderTop: 'none' }}>
          <Typography variant="caption" color="error.main" display="block" sx={{ wordBreak: 'break-all' }}>
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// 5. 企微超链接卡片渲染器
const LinkCard: React.FC<{ message: Message }> = ({ message }) => {
  let title = '分享链接';
  let description = '';
  let linkUrl = '';

  const rawJson = message.content_json;
  let parsedJson: Record<string, any> | undefined;
  if (rawJson && typeof rawJson === 'object') {
    parsedJson = rawJson as Record<string, any>;
  } else if (typeof rawJson === 'string') {
    try {
      parsedJson = JSON.parse(rawJson);
    } catch {
      // ignore
    }
  }

  let innerData = parsedJson || {};
  if (parsedJson) {
    // 优先尝试从 raw_data 解析（企业微信原生消息结构）
    const rawData = parsedJson.raw_data;
    if (rawData) {
      if (typeof rawData === 'string') {
        try {
          innerData = JSON.parse(rawData);
        } catch {
          // ignore
        }
      } else if (typeof rawData === 'object') {
        innerData = rawData;
      }
    }

    const linkData = (innerData.link || parsedJson.link) as Record<string, unknown> | undefined;
    if (linkData) {
      title = (linkData.title as string) || title;
      description = (linkData.description as string) || description;
      linkUrl = (linkData.link_url as string) || '';
    }
  }

  const handleOpenLink = () => {
    if (linkUrl) {
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box 
      onClick={handleOpenLink}
      sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        bgcolor: 'background.paper',
        maxWidth: 280,
        cursor: linkUrl ? 'pointer' : 'default',
        transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        '&:hover': linkUrl ? { 
          boxShadow: 2,
          transform: 'translateY(-2px)'
        } : {},
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'text.primary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1 }}>
          {description}
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderTop: '1px solid', borderColor: 'divider', pt: 1, mt: 0.5 }}>
        <LinkIcon fontSize="inherit" color="action" />
        <Typography variant="caption" color="text.disabled" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          网页链接
        </Typography>
      </Box>
    </Box>
  );
};

// 格式化通话记录消息 (voiptext)
const formatVoipText = (info: any): string => {
  if (!info) return '音视频通话';
  const duration = info.callduration;
  const inviteType = info.invitetype;
  
  let typeStr = '音视频通话';
  if (inviteType === 1) typeStr = '单人视频通话';
  else if (inviteType === 2) typeStr = '单人语音通话';
  else if (inviteType === 3) typeStr = '多人视频通话';
  else if (inviteType === 4) typeStr = '多人语音通话';
  
  if (duration === undefined || duration === null || duration === 0) {
    return `${typeStr} (未接通)`;
  }
  
  let durationStr = '';
  if (duration >= 3600) {
    const hrs = Math.floor(duration / 3600);
    const mins = Math.floor((duration % 3600) / 60);
    const secs = duration % 60;
    durationStr = `${hrs}小时${mins}分${secs}秒`;
  } else if (duration >= 60) {
    const mins = Math.floor(duration / 60);
    const secs = duration % 60;
    durationStr = `${mins}分${secs}秒`;
  } else {
    durationStr = `${duration}秒`;
  }
  
  return `${typeStr} (时长: ${durationStr})`;
};

const formatCallDuration = (duration: unknown): string => {
  const seconds = Number(duration || 0);
  if (!Number.isFinite(seconds) || seconds <= 0) return '未接通';
  const wholeSeconds = Math.floor(seconds);
  if (wholeSeconds >= 3600) {
    const hrs = Math.floor(wholeSeconds / 3600);
    const mins = Math.floor((wholeSeconds % 3600) / 60);
    const secs = wholeSeconds % 60;
    return `${hrs}小时${mins}分${secs}秒`;
  }
  if (wholeSeconds >= 60) {
    const mins = Math.floor(wholeSeconds / 60);
    const secs = wholeSeconds % 60;
    return `${mins}分${secs}秒`;
  }
  return `${wholeSeconds}秒`;
};

const getCallSummary = (message: Message): { title: string; subtitle: string; isVideo: boolean } => {
  let title = message.content || '音视频通话';
  let duration: unknown = undefined;
  let isVideo = /视频/.test(title);

  for (const payload of getMessagePayloadCandidates(message)) {
    const info = payload.info || payload.voiptext;
    if (info && typeof info === 'object') {
      title = formatVoipText(info);
      duration = info.callduration;
      isVideo = info.invitetype === 1 || info.invitetype === 3;
      break;
    }

    const extraInfo = payload.extra_info;
    if (extraInfo && typeof extraInfo === 'object') {
      duration = extraInfo.call_duration ?? duration;
    }

    const callInfo = payload.meeting_voice_call || payload.meeting_voicecall;
    if (callInfo && typeof callInfo === 'object') {
      title = title || '音视频通话';
    }
  }

  const subtitle = duration === undefined ? '通话记录' : `通话时长：${formatCallDuration(duration)}`;
  return { title, subtitle, isVideo };
};

const CallSummaryCard: React.FC<{ message: Message; isSelf: boolean; note?: string }> = ({ message, isSelf, note }) => {
  const summary = getCallSummary(message);
  const iconColor = isSelf ? 'rgba(255, 255, 255, 0.9)' : (summary.isVideo ? 'info.main' : 'primary.main');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180, maxWidth: 320 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isSelf ? 'rgba(255,255,255,0.14)' : 'rgba(25,118,210,0.08)',
          flexShrink: 0,
        }}
      >
        {summary.isVideo ? (
          <VideocamIcon fontSize="small" sx={{ color: iconColor }} />
        ) : (
          <CallIcon fontSize="small" sx={{ color: iconColor }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: isSelf ? '#fff' : 'text.primary', lineHeight: 1.4 }}>
          {summary.title}
        </Typography>
        <Typography variant="caption" sx={{ color: isSelf ? 'rgba(255,255,255,0.72)' : 'text.secondary', lineHeight: 1.4 }}>
          {note || summary.subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

// 个人名片渲染器 (card)
const PersonalCard: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  let corpname = '';
  let userid = '';
  let cardName = '';

  const isExternalContactId = (value?: string) =>
    Boolean(value && /^(wm|wb|wr|external_userid_)/.test(value));

  const looksLikeMachineId = (value?: string) => {
    if (!value) return true;
    const text = value.trim();
    if (!text) return true;
    if (/^\d{4,}$/.test(text)) return true;
    if (/^[a-zA-Z0-9_-]{20,}$/.test(text)) return true;
    return isExternalContactId(text);
  };

  const rawJson = message.content_json;
  let parsedJson: Record<string, any> | undefined;
  if (rawJson && typeof rawJson === 'object') {
    parsedJson = rawJson as Record<string, any>;
  } else if (typeof rawJson === 'string') {
    try {
      parsedJson = JSON.parse(rawJson);
    } catch {
      // ignore
    }
  }

  let innerData = parsedJson || {};
  if (parsedJson) {
    const rawData = parsedJson.raw_data;
    if (rawData) {
      if (typeof rawData === 'string') {
        try {
          innerData = JSON.parse(rawData);
        } catch {}
      } else if (typeof rawData === 'object') {
        innerData = rawData;
      }
    }
    const cardData = (innerData.card || parsedJson.card) as Record<string, unknown> | undefined;
    if (cardData) {
      corpname = (cardData.corpname as string) || '';
      userid = (cardData.userid as string) || '';
      cardName = (
        (cardData.name as string) ||
        (cardData.nickname as string) ||
        (cardData.display_name as string) ||
        (cardData.username as string) ||
        ''
      );
    }
  }

  // 从 content 解析名片信息
  if (!userid && message.content) {
    const match = message.content.match(/^\[名片\]\s*(.*?)\/([a-zA-Z0-9_-]+)$/);
    if (match) {
      corpname = match[1] || '';
      userid = match[2] || '';
    }
  }

  const displayName = !looksLikeMachineId(cardName)
    ? cardName
    : (corpname || (isExternalContactId(userid) ? '外部联系人名片' : '个人名片'));
  const shouldRenderOpenDataName = Boolean(userid && !isExternalContactId(userid));

  if (!userid && !displayName) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CardIcon fontSize="small" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.9)' : 'inherit' }} />
        <Typography variant="body2">[名片]</Typography>
      </Box>
    );
  }

  const nameHtml = shouldRenderOpenDataName
    ? renderWeComUserNameHtml(userid, displayName)
    : renderWeComUserNameHtml(undefined, displayName);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: 'background.paper',
        width: 240,
        maxWidth: '100%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
          <CardIcon sx={{ color: 'primary.main' }} />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            dangerouslySetInnerHTML={{ __html: nameHtml }}
          />
          {corpname && corpname !== displayName && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={corpname}
            >
              {corpname}
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="caption" color="text.disabled">
          个人名片
        </Typography>
        <Typography variant="caption" color="primary.main" fontWeight="500">
          企微联系人
        </Typography>
      </Box>
    </Box>
  );
};

// 地理位置渲染器 (location)
const LocationCard: React.FC<{ message: Message }> = ({ message }) => {
  let title = '';
  let address = '';
  let longitude: number | null = null;
  let latitude: number | null = null;

  const rawJson = message.content_json;
  let parsedJson: Record<string, any> | undefined;
  if (rawJson && typeof rawJson === 'object') {
    parsedJson = rawJson as Record<string, any>;
  } else if (typeof rawJson === 'string') {
    try {
      parsedJson = JSON.parse(rawJson);
    } catch {
      // ignore
    }
  }

  let innerData = parsedJson || {};
  if (parsedJson) {
    const rawData = parsedJson.raw_data;
    if (rawData) {
      if (typeof rawData === 'string') {
        try {
          innerData = JSON.parse(rawData);
        } catch {}
      } else if (typeof rawData === 'object') {
        innerData = rawData;
      }
    }
    const locData = (innerData.location || parsedJson.location) as Record<string, unknown> | undefined;
    if (locData) {
      title = (locData.title as string) || '';
      address = (locData.address as string) || '';
      longitude = typeof locData.longitude === 'number' ? locData.longitude : parseFloat(locData.longitude as string) || null;
      latitude = typeof locData.latitude === 'number' ? locData.latitude : parseFloat(locData.latitude as string) || null;
    }
  }

  // 从 content 解析位置信息
  if (!title && message.content) {
    const match = message.content.match(/^\[位置\]\s*(.*?)\s*\(([-+]?\d+(?:\.\d+)?),\s*([-+]?\d+(?:\.\d+)?)\)$/);
    if (match) {
      title = match[1] || '位置';
      longitude = parseFloat(match[2]);
      latitude = parseFloat(match[3]);
    }
  }

  if (!title && message.address) {
    title = message.address;
  }

  title = title || '位置信息';
  address = address || message.address || '暂无详细地址';

  // 构建高德地图导航链接
  const mapUrl = longitude && latitude
    ? `https://uri.amap.com/marker?position=${longitude},${latitude}&name=${encodeURIComponent(title)}`
    : null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        width: 240,
        maxWidth: '100%',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          color="text.primary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 0.25
          }}
          title={title}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            height: 32,
            lineHeight: 1.3,
            mb: 1
          }}
          title={address}
        >
          {address}
        </Typography>
      </Box>

      {/* 简易地图预览框 */}
      <Box
        sx={{
          height: 90,
          bgcolor: '#e5e3df',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          backgroundImage: 'radial-gradient(circle, #f2efe9 10%, transparent 10%), radial-gradient(circle, #f2efe9 10%, transparent 10%)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 10px 10px',
        }}
      >
        <Box sx={{ position: 'absolute', width: '150%', height: 2, bgcolor: '#fff', transform: 'rotate(15deg)', top: '30%' }} />
        <Box sx={{ position: 'absolute', width: '150%', height: 2, bgcolor: '#fff', transform: 'rotate(-25deg)', top: '60%' }} />
        <Box sx={{ position: 'absolute', width: 2, height: '150%', bgcolor: '#fff', transform: 'rotate(10deg)', left: '40%' }} />
        <Box sx={{ position: 'absolute', width: 2, height: '150%', bgcolor: '#fff', transform: 'rotate(-40deg)', left: '70%' }} />

        {/* 呼吸脉冲波 */}
        <Box
          sx={{
            position: 'absolute',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'rgba(25, 118, 210, 0.2)',
            animation: 'pulse 2s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { transform: 'scale(0.5)', opacity: 0 },
              '50%': { opacity: 0.5 },
              '100%': { transform: 'scale(1.5)', opacity: 0 }
            }
          }}
        />

        <LocationIcon color="error" sx={{ fontSize: 32, zIndex: 1, filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.3))' }} />
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 0.8,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="caption" color="text.disabled">
          {longitude && latitude ? `${longitude.toFixed(4)}, ${latitude.toFixed(4)}` : '位置分享'}
        </Typography>
        {mapUrl ? (
          <Link
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{
              fontWeight: 'bold',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            打开地图
          </Link>
        ) : (
          <Typography variant="caption" color="text.disabled">
            位置分享
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// 文本超链接 & Markdown 粗体格式化渲染器
const LinkifyAndFormatText: React.FC<{ text: string; isSelf: boolean }> = ({ text, isSelf }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'inline', color: 'inherit' }}>
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          return (
            <Link
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: isSelf ? '#dceefe' : 'primary.main',
                textDecoration: 'underline',
                wordBreak: 'break-all',
                fontWeight: 500,
                '&:hover': { color: isSelf ? '#FFF' : 'primary.dark' }
              }}
            >
              {part}
            </Link>
          );
        }

        // 解析粗体 **text**
        const boldRegex = /(\*\*[^*]+\*\*)/g;
        const subParts = part.split(boldRegex);
        return subParts.map((subPart, subIdx) => {
          if (subPart.startsWith('**') && subPart.endsWith('**')) {
            const boldVal = subPart.slice(2, -2);
            return (
              <Box component="span" key={`${index}-${subIdx}`} sx={{ fontWeight: 'bold' }}>
                {replaceWeChatEmoticons(boldVal)}
              </Box>
            );
          }
          return replaceWeChatEmoticons(subPart);
        });
      })}
    </Typography>
  );
};

// 提取 Markdown 文本内容的辅助函数
const getMarkdownText = (message: Message): string => {
  const contentJson = message.content_json;
  let parsedJson: any = null;
  if (contentJson && typeof contentJson === 'object') {
    parsedJson = contentJson;
  } else if (typeof contentJson === 'string') {
    try {
      parsedJson = JSON.parse(contentJson);
    } catch {
      // ignore
    }
  }

  if (parsedJson) {
    if (parsedJson.info?.content) {
      return parsedJson.info.content;
    }
    if (parsedJson.raw_data) {
      try {
        const raw = typeof parsedJson.raw_data === 'string'
          ? JSON.parse(parsedJson.raw_data)
          : parsedJson.raw_data;
        if (raw?.info?.content) {
          return raw.info.content;
        }
      } catch {
        // ignore
      }
    }
    if (parsedJson.msgtype === 'markdown' && parsedJson.markdown?.content) {
      return parsedJson.markdown.content;
    }
  }

  return message.content || '';
};

// Markdown 渲染组件
const MarkdownRenderer: React.FC<{ text: string; isSelf: boolean }> = ({ text, isSelf }) => {
  if (!text) return null;

  const lines = text.split('\n');

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: isSelf ? '#fff' : 'text.primary',
      wordBreak: 'break-all',
      whiteSpace: 'pre-wrap',
      '& strong': {
        fontWeight: 'bold',
        color: isSelf ? '#fff' : 'text.primary'
      },
      '& blockquote': {
        borderLeft: '4px solid',
        borderColor: isSelf ? 'rgba(255,255,255,0.45)' : 'primary.main',
        pl: 1,
        ml: 0,
        my: 0.5,
        color: isSelf ? 'rgba(255,255,255,0.85)' : 'text.secondary',
        fontStyle: 'italic'
      }
    }}>
      {lines.map((line, idx) => {
        // 引用块
        if (line.startsWith('>')) {
          const content = line.substring(1).trim();
          return (
            <Box component="blockquote" key={idx}>
              <LinkifyAndFormatText text={content} isSelf={isSelf} />
            </Box>
          );
        }

        // 标题渲染
        if (line.startsWith('#')) {
          const match = line.match(/^(#{1,6})\s+(.*)$/);
          if (match) {
            const level = match[1].length;
            const content = match[2];
            const variant = level === 1 ? 'subtitle1' : 'subtitle2';
            return (
              <Typography
                key={idx}
                variant={variant}
                fontWeight="bold"
                sx={{
                  mt: 0.75,
                  mb: 0.25,
                  color: isSelf ? '#fff' : 'text.primary',
                  lineHeight: 1.3
                }}
              >
                <LinkifyAndFormatText text={content} isSelf={isSelf} />
              </Typography>
            );
          }
        }

        // 列表渲染
        if (line.startsWith('- ') || line.startsWith('* ')) {
          const content = line.substring(2);
          return (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', pl: 0.5 }}>
              <Typography sx={{ mr: 0.75, color: isSelf ? 'rgba(255,255,255,0.7)' : 'text.secondary', userSelect: 'none' }}>•</Typography>
              <Box sx={{ flex: 1 }}>
                <LinkifyAndFormatText text={content} isSelf={isSelf} />
              </Box>
            </Box>
          );
        }

        // 普通文本行
        return (
          <Box key={idx} sx={{ minHeight: '1.2em' }}>
            <LinkifyAndFormatText text={line} isSelf={isSelf} />
          </Box>
        );
      })}
    </Box>
  );
};

// 企业云盘文件卡片 (qydiskfile)
const QyDiskFileCard: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  let fileName = '';
  let url = '';

  const rawJson = message.content_json;
  let parsedJson: any = null;
  if (rawJson && typeof rawJson === 'object') {
    parsedJson = rawJson;
  } else if (typeof rawJson === 'string') {
    try {
      parsedJson = JSON.parse(rawJson);
    } catch {
      // ignore
    }
  }

  if (parsedJson) {
    let info = parsedJson.info || parsedJson.qydiskfile;
    if (!info && parsedJson.content) {
      try {
        const inner = typeof parsedJson.content === 'string'
          ? JSON.parse(parsedJson.content)
          : parsedJson.content;
        info = inner?.info || inner?.qydiskfile || inner;
      } catch {}
    }
    if (!info && parsedJson.raw_data) {
      try {
        const inner = typeof parsedJson.raw_data === 'string'
          ? JSON.parse(parsedJson.raw_data)
          : parsedJson.raw_data;
        info = inner?.info || inner?.qydiskfile || inner;
      } catch {}
    }
    if (info) {
      fileName = info.filename || info.file_name || '';
      url = info.url || '';
    }
  }

  if (!fileName) {
    fileName = message.file_name || '企业云盘文件';
  }

  const ext = fileName.split('.').pop()?.toLowerCase() || '';

  let icon = <FileIcon sx={{ fontSize: 28, color: '#00897b' }} />;
  let iconBg = 'rgba(0, 137, 123, 0.08)';

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
    icon = <ImageIcon sx={{ fontSize: 28, color: '#00bcd4' }} />;
    iconBg = 'rgba(0, 188, 212, 0.08)';
  } else if (ext === 'pdf') {
    icon = <FileIcon sx={{ fontSize: 28, color: '#d32f2f' }} />;
    iconBg = 'rgba(211, 47, 47, 0.08)';
  } else if (['doc', 'docx'].includes(ext)) {
    icon = <FileIcon sx={{ fontSize: 28, color: '#1976d2' }} />;
    iconBg = 'rgba(25, 118, 210, 0.08)';
  } else if (['xls', 'xlsx'].includes(ext)) {
    icon = <FileIcon sx={{ fontSize: 28, color: '#2e7d32' }} />;
    iconBg = 'rgba(46, 125, 50, 0.08)';
  }

  if (isSelf) {
    iconBg = 'rgba(255, 255, 255, 0.15)';
    icon = React.cloneElement(icon, { sx: { ...icon.props.sx, color: '#fff' } });
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 280,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isSelf ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.08)',
        bgcolor: isSelf ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.01)',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        transition: 'all 0.2s ease-in-out',
        cursor: url ? 'pointer' : 'default',
        '&:hover': url ? {
          bgcolor: isSelf ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.03)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transform: 'translateY(-1px)'
        } : {}
      }}
      onClick={() => {
        if (url) {
          window.open(url, '_blank');
        }
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, alignItems: 'center' }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '6px',
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color: isSelf ? '#fff' : 'text.primary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-all',
              lineHeight: 1.3
            }}
            title={fileName}
          >
            {fileName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              color: isSelf ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
            }}
          >
            企业云盘文件
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// 微信小程序卡片 (weapp)
const WeappCard: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  let title = '';
  let displayname = '';

  const rawJson = message.content_json;
  let parsedJson: any = null;
  if (rawJson && typeof rawJson === 'object') {
    parsedJson = rawJson;
  } else if (typeof rawJson === 'string') {
    try {
      parsedJson = JSON.parse(rawJson);
    } catch {
      // ignore
    }
  }

  if (parsedJson) {
    let weapp = parsedJson.weapp;
    if (!weapp && parsedJson.raw_data) {
      try {
        const inner = typeof parsedJson.raw_data === 'string'
          ? JSON.parse(parsedJson.raw_data)
          : parsedJson.raw_data;
        weapp = inner?.weapp;
      } catch {}
    }
    if (weapp) {
      title = weapp.title || '';
      displayname = weapp.displayname || '';
    }
  }

  if (!title) {
    title = message.content || '微信小程序';
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 250,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isSelf ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.08)',
        bgcolor: isSelf ? 'rgba(255, 255, 255, 0.04)' : 'background.paper',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transform: 'translateY(-1px)'
        }
      }}
    >
      {/* Header */}
      {displayname && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, pb: 0.75, borderBottom: '1px solid', borderColor: isSelf ? 'rgba(255, 255, 255, 0.1)' : 'action.hover' }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#07C160', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ color: '#fff', fontSize: '10px', fontWeight: 'bold', transform: 'scale(0.8)' }}>小</Typography>
          </Box>
          <Typography variant="caption" fontWeight="bold" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayname}
          </Typography>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ p: 1.5 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            color: isSelf ? '#fff' : 'text.primary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.4
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: isSelf ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.02)', borderTop: '1px solid', borderColor: isSelf ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
        <Typography variant="caption" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{ fontSize: '0.8rem' }}>💚</span> 小程序
        </Typography>
      </Box>
    </Box>
  );
};

// 视频号卡片 (sphfeed)
const SphFeedCard: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  let sph_name = '';
  let feed_desc = '';

  const rawJson = message.content_json;
  let parsedJson: any = null;
  if (rawJson && typeof rawJson === 'object') {
    parsedJson = rawJson;
  } else if (typeof rawJson === 'string') {
    try {
      parsedJson = JSON.parse(rawJson);
    } catch {
      // ignore
    }
  }

  if (parsedJson) {
    let sphfeed = parsedJson.sphfeed;
    if (!sphfeed && parsedJson.raw_data) {
      try {
        const inner = typeof parsedJson.raw_data === 'string'
          ? JSON.parse(parsedJson.raw_data)
          : parsedJson.raw_data;
        sphfeed = inner?.sphfeed;
      } catch {}
    }
    if (sphfeed) {
      sph_name = sphfeed.sph_name || '';
      feed_desc = sphfeed.feed_desc || '';
    }
  }

  if (!sph_name && message.content) {
    const match = message.content.match(/^\[视频号\]\s*(.*?):\s*(.*)$/s);
    if (match) {
      sph_name = match[1];
      feed_desc = match[2];
    }
  }

  if (!sph_name) {
    sph_name = '微信视频号';
  }
  if (!feed_desc) {
    feed_desc = message.content || '';
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 250,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isSelf ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.08)',
        bgcolor: isSelf ? 'rgba(255, 255, 255, 0.04)' : 'background.paper',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transform: 'translateY(-1px)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, pb: 0.75, borderBottom: '1px solid', borderColor: isSelf ? 'rgba(255, 255, 255, 0.1)' : 'action.hover' }}>
        <VideocamIcon fontSize="small" sx={{ color: isSelf ? '#fff' : '#FF9800' }} />
        <Typography variant="caption" fontWeight="bold" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.9)' : 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sph_name}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ p: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: isSelf ? '#fff' : 'text.primary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
          title={feed_desc}
        >
          {feed_desc}
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: isSelf ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.02)', borderTop: '1px solid', borderColor: isSelf ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
        <Typography variant="caption" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{ fontSize: '0.8rem' }}>🎬</span> 视频号
        </Typography>
      </Box>
    </Box>
  );
};

// 系统通知列表渲染器 (note)
const NoteMessageRenderer: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  let items: any[] = [];
  const contentJson = message.content_json;
  let parsedJson: any = null;
  if (contentJson && typeof contentJson === 'object') {
    parsedJson = contentJson;
  } else if (typeof contentJson === 'string') {
    try {
      parsedJson = JSON.parse(contentJson);
    } catch {
      // ignore
    }
  }

  if (parsedJson) {
    let info = parsedJson.info || parsedJson.note;
    if (!info && parsedJson.raw_data) {
      try {
        const inner = typeof parsedJson.raw_data === 'string'
          ? JSON.parse(parsedJson.raw_data)
          : parsedJson.raw_data;
        info = inner?.info || inner?.note;
      } catch {
        // ignore
      }
    }
    if (info && Array.isArray(info.items)) {
      items = info.items;
    }
  }

  if (items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.85)' : 'text.secondary' }}>
        {message.content || '[系统通知]'}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((item: any, idx: number) => {
        let itemContent: any = null;
        if (typeof item.content === 'string') {
          try {
            itemContent = JSON.parse(item.content);
          } catch {
            itemContent = item.content;
          }
        } else {
          itemContent = item.content;
        }

        const type = item.msg_type || item.type || '';

        if (type === 'text') {
          const textVal = itemContent?.content || item.content || '';
          return <LinkifyText key={idx} text={textVal} isSelf={isSelf} />;
        } else if (type === 'image') {
          const sdkFileId = itemContent?.sdkfileid || '';
          return (
            <Box key={idx} sx={{ mt: 0.5, mb: 0.5 }}>
              <MediaImage message={message} sdkFileId={sdkFileId} isSelf={isSelf} />
            </Box>
          );
        } else if (type === 'location') {
          const mockMsg: Message = {
            ...message,
            msg_type: 'location',
            content_json: itemContent
          };
          return <LocationCard key={idx} message={mockMsg} />;
        } else if (type === 'link') {
          const mockMsg: Message = {
            ...message,
            msg_type: 'link',
            content_json: itemContent
          };
          return <LinkCard key={idx} message={mockMsg} />;
        } else {
          return (
            <Typography key={idx} variant="body2" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.85)' : 'text.secondary' }}>
              [{type || '未知'}消息]
            </Typography>
          );
        }
      })}
    </Box>
  );
};

// 聊天记录子项消息体渲染适配器
const renderChatRecordItemBody = (
  type: string,
  itemContent: any,
  parentMessage: Message
) => {
  switch (type) {
    case 'ChatRecordText': {
      const textVal = itemContent?.content || '';
      return <LinkifyText text={textVal} isSelf={false} />;
    }
    case 'ChatRecordImage': {
      const sdkFileId = itemContent?.sdkfileid || '';
      return (
        <Box sx={{ maxWidth: '100%', mt: 0.5 }}>
          <MediaImage message={parentMessage} sdkFileId={sdkFileId} isSelf={false} />
        </Box>
      );
    }
    case 'ChatRecordLocation': {
      const mockMsg: Message = {
        ...parentMessage,
        msg_type: 'location',
        content_json: itemContent,
      };
      return <LocationCard message={mockMsg} />;
    }
    case 'ChatRecordLink': {
      const mockMsg: Message = {
        ...parentMessage,
        msg_type: 'link',
        content_json: itemContent,
      };
      return <LinkCard message={mockMsg} />;
    }
    default:
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          [{type || '未知'} 消息]
        </Typography>
      );
  }
};

// 聊天记录卡片渲染组件
const ChatRecordCard: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  const [open, setOpen] = useState(false);

  let chatrecordObj: any = message.content_json?.chatrecord;
  if (!chatrecordObj && message.content_json?.raw_data) {
    try {
      const inner = typeof message.content_json.raw_data === 'string'
        ? JSON.parse(message.content_json.raw_data)
        : message.content_json.raw_data;
      chatrecordObj = inner?.chatrecord;
    } catch {
      // ignore
    }
  }

  const title = (chatrecordObj?.title as string) || message.chatrecord_title || message.content || '聊天记录';
  const items = Array.isArray(chatrecordObj?.item) ? chatrecordObj.item : [];

  const getPreviewText = (item: any) => {
    let itemContent: any = null;
    if (typeof item.content === 'string') {
      try {
        itemContent = JSON.parse(item.content);
      } catch {
        itemContent = item.content;
      }
    } else {
      itemContent = item.content;
    }

    switch (item.type) {
      case 'ChatRecordText':
        return itemContent?.content || '';
      case 'ChatRecordImage':
        return '[图片]';
      case 'ChatRecordLocation':
        return `[位置] ${itemContent?.address || itemContent?.title || ''}`;
      case 'ChatRecordLink':
        return `[链接] ${itemContent?.title || ''}`;
      default:
        return `[${item.type || '未知'}消息]`;
    }
  };

  const handleOpen = () => {
    if (items.length > 0) {
      setOpen(true);
    }
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 1.5,
          bgcolor: isSelf ? 'rgba(255, 255, 255, 0.12)' : 'background.paper',
          border: '1px solid',
          borderColor: isSelf ? 'rgba(255, 255, 255, 0.2)' : 'divider',
          borderRadius: 2,
          cursor: items.length > 0 ? 'pointer' : 'default',
          width: 280,
          maxWidth: '100%',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          transition: 'all 0.2s',
          '&:hover': items.length > 0 ? {
            borderColor: isSelf ? 'rgba(255, 255, 255, 0.4)' : 'primary.main',
            bgcolor: isSelf ? 'rgba(255, 255, 255, 0.18)' : 'action.hover',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          } : {},
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '1.2rem', flexShrink: 0 }}>📋</Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{
              color: isSelf ? '#fff' : 'text.primary',
              flexGrow: 1,
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Content Preview */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            mb: 1,
            borderBottom: '1px solid',
            borderColor: isSelf ? 'rgba(255, 255, 255, 0.1)' : 'divider',
            pb: 1,
          }}
        >
          {items.slice(0, 3).map((item: any, idx: number) => (
            <Typography
              key={idx}
              variant="caption"
              noWrap
              sx={{
                color: isSelf ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
                lineHeight: 1.4,
              }}
            >
              {getPreviewText(item)}
            </Typography>
          ))}
          {items.length > 3 && (
            <Typography
              variant="caption"
              sx={{
                color: isSelf ? 'rgba(255, 255, 255, 0.5)' : 'text.disabled',
                fontStyle: 'italic',
              }}
            >
              ... 等共 {items.length} 条记录
            </Typography>
          )}
          {items.length === 0 && (
            <Typography
              variant="caption"
              sx={{
                color: isSelf ? 'rgba(255, 255, 255, 0.5)' : 'text.disabled',
                fontStyle: 'italic',
              }}
            >
              无聊天记录内容
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            color: isSelf ? 'rgba(255, 255, 255, 0.5)' : 'text.disabled',
            alignSelf: 'flex-start',
          }}
        >
          聊天记录
        </Typography>
      </Box>

      {/* Dialog to view full chat records */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {/* Custom Dialog Title */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ maxWidth: '80%' }}>
            {title}
          </Typography>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Dialog Content */}
        <DialogContent
          sx={{
            p: 2.5,
            overflowY: 'auto',
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {items.map((item: any, idx: number) => {
            let itemContent: any = null;
            if (typeof item.content === 'string') {
              try {
                itemContent = JSON.parse(item.content);
              } catch {
                itemContent = item.content;
              }
            } else {
              itemContent = item.content;
            }

            const timeStr = item.msgtime ? formatDateTime(item.msgtime * 1000) : '';

            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  p: 1.5,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  border: '1px solid',
                  borderColor: 'grey.100',
                }}
              >
                {/* Sub-item Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 500 }}>
                    第 {idx + 1} 条记录
                  </Typography>
                  {timeStr && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {timeStr}
                    </Typography>
                  )}
                </Box>

                {/* Sub-item Body */}
                <Box sx={{ mt: 0.5 }}>
                  {renderChatRecordItemBody(item.type, itemContent, message)}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </>
  );
};

// 根据消息类型渲染内容
const renderMessageContent = (message: Message, isSelf: boolean) => {
  switch (message.msg_type) {
    case 'text': {
      const displayInfo = extractWeComDisplayInfo(message);
      if (displayInfo) {
        return (
          <WeComMessageDisplay
            msgid={displayInfo.msgid}
            secretKey={displayInfo.secretKey}
            fallback={message.content || '[文本消息]'}
            isSelf={isSelf}
            displayType="text"
            messageType={message.msg_type}
          />
        );
      }
      return <LinkifyText text={message.content} isSelf={isSelf} />;
    }

    case 'chatrecord':
    case 'forward':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <ChatRecordCard message={message} isSelf={isSelf} />;

    case 'mixed': {
      const nativeDisplay = renderWeComNativeMessage(message, isSelf);
      if (nativeDisplay) return nativeDisplay;

      let items: any[] = [];
      const contentJson = message.content_json;
      let parsedJson: any = null;
      if (contentJson && typeof contentJson === 'object') {
        parsedJson = contentJson;
      } else if (typeof contentJson === 'string') {
        try {
          parsedJson = JSON.parse(contentJson);
        } catch {
          // ignore
        }
      }
      
      if (parsedJson) {
        let mixed = parsedJson.mixed;
        if (!mixed && parsedJson.raw_data) {
          try {
            const inner = typeof parsedJson.raw_data === 'string'
              ? JSON.parse(parsedJson.raw_data)
              : parsedJson.raw_data;
            mixed = inner?.mixed;
          } catch {
            // ignore
          }
        }
        if (mixed && Array.isArray(mixed.item)) {
          items = mixed.item;
        }
      }

      if (items.length === 0) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.85)' : 'text.secondary' }}>[{message.content || '混合消息'}]</Typography>
          </Box>
        );
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item: any, idx: number) => {
            let itemContent: any = null;
            if (typeof item.content === 'string') {
              try {
                itemContent = JSON.parse(item.content);
              } catch {
                itemContent = item.content;
              }
            } else {
              itemContent = item.content;
            }

            if (item.type === 'text') {
              const textVal = itemContent?.content || item.content || '';
              return <LinkifyText key={idx} text={textVal} isSelf={isSelf} />;
            } else if (item.type === 'image') {
              const sdkFileId = itemContent?.sdkfileid || '';
              return (
                <Box key={idx} sx={{ mt: 0.5, mb: 0.5 }}>
                  <MediaImage message={message} sdkFileId={sdkFileId} isSelf={isSelf} />
                </Box>
              );
            } else {
              return (
                <Typography key={idx} variant="body2" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.85)' : 'text.secondary' }}>
                  [{item.type || '未知类型'}消息]
                </Typography>
              );
            }
          })}
        </Box>
      );
    }

    case 'image': {
      const nativeDisplay = renderWeComNativeMessage(message, isSelf);
      if (nativeDisplay) return nativeDisplay;
      return <MediaImage message={message} isSelf={isSelf} />;
    }

    case 'audio_archive':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      if (!hasPlayableAudioMedia(message)) {
        return <CallSummaryCard message={message} isSelf={isSelf} note="暂无可播放录音" />;
      }
      return <MediaAudio message={message} isSelf={isSelf} label={message.content || '音频存档'} />;

    case 'meeting_voice_call':
    case 'meeting_voicecall':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      if (!hasPlayableAudioMedia(message)) {
        return <CallSummaryCard message={message} isSelf={isSelf} />;
      }
      return <MediaAudio message={message} isSelf={isSelf} label={getCallSummary(message).title} />;

    case 'meeting_notification': {
      let displayContent = message.content || '[会议通知]';
      const contentJson = message.content_json;
      let parsedJson: any = null;
      if (contentJson && typeof contentJson === 'object') {
        parsedJson = contentJson;
      } else if (typeof contentJson === 'string') {
        try {
          parsedJson = JSON.parse(contentJson);
        } catch {
          // ignore
        }
      }
      
      if (parsedJson) {
        let info = parsedJson.info || parsedJson.meeting_notification;
        if (!info && parsedJson.raw_data) {
          try {
            const inner = typeof parsedJson.raw_data === 'string'
              ? JSON.parse(parsedJson.raw_data)
              : parsedJson.raw_data;
            info = inner?.info || inner?.meeting_notification;
          } catch {
            // ignore
          }
        }
        
        if (info && info.content) {
          displayContent = `[会议通知] ${info.content}`;
        }
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon fontSize="small" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.9)' : 'info.main' }} />
          <Typography variant="body2">{displayContent}</Typography>
        </Box>
      );
    }

    case 'meeting': {
      const nativeDisplay = renderWeComNativeMessage(message, isSelf);
      if (nativeDisplay) return nativeDisplay;

      let displayContent = message.content || '[会议]';
      const contentJson = message.content_json;
      let parsedJson: any = null;
      if (contentJson && typeof contentJson === 'object') {
        parsedJson = contentJson;
      } else if (typeof contentJson === 'string') {
        try {
          parsedJson = JSON.parse(contentJson);
        } catch {
          // ignore
        }
      }
      
      if (parsedJson) {
        let meeting = parsedJson.meeting;
        if (!meeting && parsedJson.raw_data) {
          try {
            const inner = typeof parsedJson.raw_data === 'string'
              ? JSON.parse(parsedJson.raw_data)
              : parsedJson.raw_data;
            meeting = inner?.meeting;
          } catch {
            // ignore
          }
        }
        
        if (meeting && meeting.topic) {
          displayContent = `[会议] ${meeting.topic}`;
        }
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon fontSize="small" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.9)' : 'info.main' }} />
          <Typography variant="body2">{displayContent}</Typography>
        </Box>
      );
    }

    case 'voiptext': {
      let displayContent = message.content || '[语音文字]';
      const contentJson = message.content_json;
      let parsedJson: any = null;
      if (contentJson && typeof contentJson === 'object') {
        parsedJson = contentJson;
      } else if (typeof contentJson === 'string') {
        try {
          parsedJson = JSON.parse(contentJson);
        } catch {
          // ignore
        }
      }
      
      if (parsedJson) {
        let info = parsedJson.info;
        if (!info && parsedJson.raw_data) {
          try {
            const inner = typeof parsedJson.raw_data === 'string'
              ? JSON.parse(parsedJson.raw_data)
              : parsedJson.raw_data;
            info = inner?.info;
          } catch {
            // ignore
          }
        }
        
        if (info) {
          displayContent = formatVoipText(info);
        }
      }

      if (hasPlayableAudioMedia(message)) {
        return <MediaAudio message={message} isSelf={isSelf} label={displayContent} />;
      }

      return <CallSummaryCard message={{ ...message, content: displayContent }} isSelf={isSelf} />;
    }

    case 'emoji':
      return (
        <Typography variant="body2" sx={{ fontSize: '1.25rem' }}>
          {message.content || '😊'}
        </Typography>
      );

    case 'emotion':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <MediaEmotion message={message} isSelf={isSelf} />;

    case 'agree':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.95)' : 'success.main', fontWeight: 500 }}>[同意]</Typography>
        </Box>
      );

    case 'disagree':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.95)' : 'error.main', fontWeight: 500 }}>[不同意]</Typography>
        </Box>
      );

    case 'voice':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <MediaAudio message={message} isSelf={isSelf} />;

    case 'video':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <MediaVideo message={message} isSelf={isSelf} />;

    case 'file':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <MediaFile message={message} isSelf={isSelf} />;

    case 'link':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <LinkCard message={message} />;

    case 'location':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <LocationCard message={message} />;

    case 'redpacket':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RedPacketIcon fontSize="small" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.9)' : '#E74C3C' }} />
          <Typography variant="body2">[红包]</Typography>
        </Box>
      );

    case 'card':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <PersonalCard message={message} isSelf={isSelf} />;

    case 'qydiskfile':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <QyDiskFileCard message={message} isSelf={isSelf} />;

    case 'markdown':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <MarkdownRenderer text={getMarkdownText(message)} isSelf={isSelf} />;

    case 'weapp':
    case 'miniprogram':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <WeappCard message={message} isSelf={isSelf} />;

    case 'sphfeed':
    case 'sph':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <SphFeedCard message={message} isSelf={isSelf} />;

    case 'voip_doc_share':
    case 'voipdocshare':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <QyDiskFileCard message={message} isSelf={isSelf} />;

    case 'todo':
    case 'vote':
    case 'collect':
    case 'solitaire':
    case 'docmsg':
    case 'news':
    case 'calendar':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return (
        <Typography variant="body2">
          {message.content || `[${message.msg_type}消息]`}
        </Typography>
      );

    case 'note':
      {
        const nativeDisplay = renderWeComNativeMessage(message, isSelf);
        if (nativeDisplay) return nativeDisplay;
      }
      return <NoteMessageRenderer message={message} isSelf={isSelf} />;

    case 'unsupported':
      return (
        <Typography variant="body2">
          {message.content || '[暂不支持的消息类型]'}
        </Typography>
      );

    case 'system':
      return (
        <Typography variant="caption" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.85)' : 'text.secondary', fontStyle: 'italic' }}>
          {message.content}
        </Typography>
      );

    case 'revoke':
      return (
        <Typography variant="caption" sx={{ color: isSelf ? 'rgba(255, 255, 255, 0.85)' : 'text.secondary', fontStyle: 'italic' }}>
          撤回了一条消息
        </Typography>
      );

    default:
      return <Typography variant="body2">{message.content || '[未知消息类型]'}</Typography>;
  }
};

// 通过 API 加载受认证保护的图片
const MediaImage: React.FC<{ message: Message; sdkFileId?: string; isSelf?: boolean }> = ({ message, sdkFileId, isSelf }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const cancelledRef = React.useRef(false);
  const retryCountRef = React.useRef(0);
  const MAX_RETRIES = 2;
  const effectiveSdkFileId = sdkFileId || extractMessageSdkFileId(message);
  const hasDownloadableSource = Boolean(message.media_url || effectiveSdkFileId);

  const loadImage = React.useCallback(async (isRetry = false) => {
    if (!message.id) {
      setLoading(false);
      setError('消息ID缺失');
      return;
    }
    if (!hasDownloadableSource) {
      setLoading(false);
      setError('该图片需通过企业微信会话展示组件查看');
      return;
    }
    if (!isRetry) {
      setLoading(true);
      setError(null);
    }
    cancelledRef.current = false;

    try {
      // 使用 apiClient（axios）并处理 sdk_file_id 自定义参数
      const url = effectiveSdkFileId
        ? `${API_ENDPOINTS.CONVERSATIONS.MEDIA(message.id!)}?sdk_file_id=${encodeURIComponent(effectiveSdkFileId)}`
        : API_ENDPOINTS.CONVERSATIONS.MEDIA(message.id!);
      const result = await apiClient.get(url, mediaRequestConfig as any);
      if (cancelledRef.current) return;

      // apiClient 响应拦截器会解包；这里 result 就是 Blob
      const blob = result as unknown as Blob;

      // 检查 blob 类型：如果返回的是 JSON 错误信息而非图片
      if (blob.type.includes('application/json') || blob.type.includes('text/')) {
        const text = await blob.text();
        console.warn('[MediaImage] 意外的响应类型:', blob.type, text.substring(0, 200));
        let messageText = '';
        try {
          const data = JSON.parse(text);
          messageText = data?.message || data?.detail || '';
        } catch {
          // ignore
        }
        throw new Error(messageText || `服务器返回了 ${blob.type} 而非图片`);
      }

      // 验证 blob 有实际数据
      if (blob.size === 0) {
        throw new Error('图片数据为空');
      }

      setBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setLoading(false);
      setError(null);
    } catch (err: any) {
      if (cancelledRef.current) return;
      const errMsg = await getFriendlyErrorMessage(err);
      console.warn(`[MediaImage] 消息 ${message.id} 图片加载失败:`, errMsg);
      setError(errMsg);
      setLoading(false);
      // 网络错误自动重试
      if (retryCountRef.current < MAX_RETRIES && !isRetry) {
        retryCountRef.current++;
        setTimeout(() => loadImage(true), 1000 * retryCountRef.current);
      }
    }
  }, [message.id, effectiveSdkFileId, hasDownloadableSource]);

  React.useEffect(() => {
    retryCountRef.current = 0;
    loadImage();
    return () => { cancelledRef.current = true; };
  }, [loadImage]);

  // 清理 blob URL
  React.useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 100, height: 100 }}>
        <CircularProgress size={24} sx={{ color: isSelf ? '#fff' : 'primary.main' }} />
      </Box>
    );
  }

  if (error || !blobUrl) {
    const isExpired = error?.includes('过期') || error?.includes('410') || error?.includes('Gone');
    const isDisplayOnly = error?.includes('展示组件');

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {isDisplayOnly ? (
          <ImageIcon sx={{ color: 'inherit', opacity: 0.7, fontSize: 32, flexShrink: 0 }} />
        ) : (
          <BrokenImageIcon sx={{ color: 'inherit', opacity: 0.7, fontSize: 32, flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.85, fontWeight: 500 }}>
            {isDisplayOnly ? '[图片]' : '[图片已失效]'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'inherit', opacity: 0.7, lineHeight: 1.3 }}>
            {error || '加载失败'}
          </Typography>
          {!isExpired && !isDisplayOnly && (
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
              <Link
                component="button"
                variant="caption"
                underline="hover"
                onClick={(e) => { e.preventDefault(); loadImage(true); }}
                sx={{ color: 'inherit', opacity: 0.8, fontWeight: 600 }}
              >
                重新加载
              </Link>
              {message.id && (
                <Link
                  href={`/api/v1${API_ENDPOINTS.CONVERSATIONS.MEDIA(message.id!)}${effectiveSdkFileId ? `?sdk_file_id=${encodeURIComponent(effectiveSdkFileId)}` : ''}`}
                  target="_blank"
                  underline="hover"
                  variant="caption"
                  sx={{ color: 'inherit', opacity: 0.8, fontWeight: 600 }}
                >
                  新窗口打开
                </Link>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: 150,
          maxWidth: '100%',
          position: 'relative',
          '&:hover .image-overlay': { opacity: 1 },
        }}
      >
        <Box
          component="img"
          src={blobUrl}
          alt="图片消息"
          onClick={() => setPreviewOpen(true)}
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 1,
            cursor: 'pointer',
            display: 'block',
          }}
        />
        {/* 悬停叠加层：放大图标 */}
        <Box
          className="image-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.3)',
            borderRadius: 1,
            opacity: 0,
            transition: 'opacity 0.2s',
            cursor: 'pointer',
          }}
          onClick={() => setPreviewOpen(true)}
        >
          <FullscreenIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
      </Box>

      {/* 全屏预览弹窗 */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.9)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            borderRadius: 2,
          },
        }}
      >
        <IconButton
          onClick={() => setPreviewOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.15)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box
            component="img"
            src={blobUrl}
            alt="图片预览"
            sx={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// 通过 API 加载鉴权保护的表情图片 (emotion)
const MediaEmotion: React.FC<{ message: Message; isSelf: boolean }> = ({ message, isSelf }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const cancelledRef = React.useRef(false);

  const loadEmotion = React.useCallback(async () => {
    if (!message.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    cancelledRef.current = false;

    try {
      const result = await apiClient.get(
        API_ENDPOINTS.CONVERSATIONS.MEDIA(message.id),
        mediaRequestConfig as any
      );
      if (cancelledRef.current) return;

      const blob = result as unknown as Blob;

      if (blob.type.includes('application/json') || blob.type.includes('text/')) {
        throw new Error('无效的表情数据');
      }

      setBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setError(null);
    } catch (err: any) {
      if (cancelledRef.current) return;
      const errMsg = await getFriendlyErrorMessage(err);
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  }, [message.id]);

  React.useEffect(() => {
    loadEmotion();
    return () => { cancelledRef.current = true; };
  }, [loadEmotion]);

  React.useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (loading) {
    return (
      <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={16} sx={{ color: isSelf ? '#fff' : 'primary.main' }} />
      </Box>
    );
  }

  if (error || !blobUrl) {
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'inherit', 
          opacity: 0.65, 
          fontStyle: 'italic',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <span>[表情已过期]</span>
      </Typography>
    );
  }

  return (
    <Box
      component="img"
      src={blobUrl}
      alt="表情"
      sx={{
        maxWidth: 100,
        maxHeight: 100,
        borderRadius: 1,
        display: 'block',
      }}
    />
  );
};


export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, sx }) => {
  const isSelf = message.direction === 'send';

  const getFriendlySenderName = (name?: string | null, userid?: string | null) => {
    const isPlaceholder = (n?: string | null) => {
      if (!n) return true;
      if (userid && n === userid) return true;
      if (n === 'undefined' || n === 'null') return true;
      if (/^\d+$/.test(n)) return true;
      if (/^(wo|wm|wr)/.test(n)) return true;
      return false;
    };

    if (!isPlaceholder(name)) {
      return name;
    }

    const targetId = userid || name;
    if (targetId) {
      if (
        message.sender_type === 'external'
        || (!message.sender_type && isExternalWeComUserId(targetId))
      ) {
        return `外部客户 (${targetId.slice(-6)})`;
      }
      if (message.sender_type === 'employee') return '员工';
      if (targetId.startsWith('wr')) {
        return '非本企业建群';
      }
      return targetId;
    }
    return '未知';
  };

  const friendlyName = getFriendlySenderName(message.sender_name, message.sender_wecom_userid || message.sender_id) || '未知';
  const senderOpenDataId = getSenderOpenDataId(message);
  const senderNameHtml = renderWeComUserNameHtml(
    senderOpenDataId || (message.sender_type === 'external' ? message.sender_wecom_userid : ''),
    friendlyName,
    message.sender_type,
  );
  const sendTime = message.send_time ? formatDateTime(message.send_time, 'yyyy-MM-dd HH:mm:ss') : '-';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSelf ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 1,
        mb: 2,
        ...sx,
      }}
    >
      {/* 头像 */}
      <WeComAvatar
        openid={senderOpenDataId}
        participantType={message.sender_type}
        src={message.sender_avatar}
        fallbackName={friendlyName}
        sx={{
          width: 36,
          height: 36,
          bgcolor: isSelf ? '#b7dcf7' : '#d8d8dc',
          color: isSelf ? '#0f172a' : 'inherit',
          fontSize: '0.875rem',
          flexShrink: 0,
        }}
      />

      {/* 消息内容 */}
      <Box
        sx={{
          maxWidth: '70%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSelf ? 'flex-end' : 'flex-start',
          gap: 0.5,
        }}
      >
        {/* 发送者名称 */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: isSelf ? 'right' : 'left' }}
          dangerouslySetInnerHTML={{ __html: senderNameHtml }}
        />

        {/* 气泡 */}
        <Box
          sx={{
            px: 2,
            py: 1,
            width: 'fit-content',
            maxWidth: '100%',
            borderRadius: 2,
            bgcolor: isSelf ? '#c9e8ff' : '#e9e9ec',
            color: 'text.primary',
            border: isSelf ? '1px solid rgba(93, 173, 226, 0.22)' : '1px solid rgba(15, 23, 42, 0.05)',
            boxShadow: '0 1px 1px rgba(15, 23, 42, 0.04)',
            wordBreak: 'break-word',
            position: 'relative',
            ...(isSelf
              ? { borderTopRightRadius: 4 }
              : { borderTopLeftRadius: 4 }),
          }}
        >
          {renderMessageContent(message, isSelf)}
          {/* 风险标记 */}
          {message.risk_flagged && (
            <Tooltip title={`风险类型: ${message.risk_types?.join(', ') || '未知'}`}>
              <WarningIcon
                fontSize="small"
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: isSelf ? -4 : undefined,
                  left: isSelf ? undefined : -4,
                  color: 'warning.main',
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  fontSize: 16,
                }}
              />
            </Tooltip>
          )}
        </Box>

        {/* 时间 */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ textAlign: isSelf ? 'right' : 'left', fontSize: '0.7rem' }}
        >
          {sendTime}
        </Typography>
      </Box>
    </Box>
  );
};
